/**
 * Admin GL management tree API
 * GET /api/gl-maps/management-tree
 * POST /api/gl-maps/:id/bill-invoice
 */

import { getStoredToken } from "../../../../services/api";
import { API_BASE_URL } from "../../../../config/api";
import type {
  GlBillInvoiceUpdateResponseDTO,
} from "../models/GlDTOs";

export { fetchGlManagementTree } from "./glManagementTreeApi";

class APIError extends Error {
  public status: number;
  public data: unknown;

  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
};

async function getToken(): Promise<string> {
  const token = getStoredToken();
  if (!token) {
    throw new APIError("No authentication token found", HTTP_STATUS.UNAUTHORIZED);
  }
  return token;
}

/**
 * Update bill/invoice flags on a leaf GL (db primary key id).
 */
export async function updateGlBillInvoiceFlags(
  glId: number,
  payload: { bill?: boolean; invoice?: boolean },
): Promise<GlBillInvoiceUpdateResponseDTO> {
  const token = await getToken();
  const response = await fetch(
    `${API_BASE_URL}/api/gl-maps/${encodeURIComponent(String(glId))}/bill-invoice`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    },
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new APIError(
      (data as { message?: string }).message ||
        `Failed to update GL mapping: ${response.statusText}`,
      response.status,
      data,
    );
  }

  return data as GlBillInvoiceUpdateResponseDTO;
}
